<template>
  <div class="page__content pms-accommodations">
    <h2 class="page__headline">{{ $tc('pms.list.title', (pagination.count || (items ? items.length : 0)), { n: (pagination.count || (items ? items.length : 0)) }) }}</h2>

    <div class="columns">
      <div class="column is-12">
        <p class="page__copy">{{$t('pms.intro') }}</p>
      </div>
    </div>

    <div class="columns is-multiline">
      <div class="column is-5">
        <label class="label">{{$t('pms.list.search')}}</label>
        <input class="input" type="text" v-model="filters.search" @input="onFilterChange" :placeholder="$t('pms.list.searchPlaceholder')" />
      </div>
      <div class="column is-2">
        <label class="label">{{$t('pms.list.status')}}</label>
        <div class="select is-fullwidth">
          <select v-model="filters.active" @change="onFilterChange">
            <option value="">{{ $t('pms.list.filterAll') }}</option>
            <option value="true">{{ $t('generic.active') }}</option>
            <option value="false">{{ $t('generic.inactive') }}</option>
          </select>
        </div>
      </div>
      <div class="column is-2">
        <label class="label">{{ $t('pms.list.unassignedOnly') }}</label>
        <div class="select is-fullwidth">
          <select v-model="filters.unassigned" @change="onFilterChange">
            <option value="">{{ $t('pms.list.filterAll') }}</option>
            <option value="unassigned">{{ $t('pms.list.onlyUnassigned') }}</option>
            <option value="assigned">{{ $t('pms.list.onlyAssigned') }}</option>
          </select>
        </div>
      </div>
      <div class="column is-1">
        <label class="label">{{ $t('pms.list.perPage') }}</label>
        <div class="select is-fullwidth">
          <select v-model.number="pagination.pageSize" @change="onPageSizeChange">
            <option :value="10">10</option>
            <option :value="25">25</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </select>
        </div>
      </div>
      <div class="column is-2 has-text-right">
        <label class="label" style="visibility:hidden;">Neu</label>
        <router-link class="button is-primary is-fullwidth" :to="{ name: 'pms-accommodations-new', params: { lang: lang }, query: { page: pagination.page } }">
          <span class="icon is-small"><i class="fa fa-plus" aria-hidden="true"></i></span>
          <span>{{ $t('pms.list.new') }}</span>
        </router-link>
      </div>
    </div>

    <div v-if="loadError" class="notification is-danger">
      {{ loadError }}
    </div>

    <div v-if="!items" class="has-text-centered">
      <span class="icon is-large"><i class="fa fa-spinner fa-spin fa-2x"></i></span>
    </div>

    <div v-else>
      <!-- Top pagination -->
      <div class="columns" v-if="pagination.count > pagination.pageSize">
        <div class="column is-12">
          <nav class="pagination is-right" role="navigation" aria-label="pagination">
            <a class="pagination-previous" :class="{ 'is-disabled': loading || pagination.page <= 1 }" @click.prevent="goToPage(1)" :title="$t('pms.list.first')">
              <span class="icon"><i class="fa fa-angle-double-left" aria-hidden="true"></i></span>
            </a>
            <a class="pagination-previous" :class="{ 'is-disabled': loading || pagination.page <= 1 }" @click.prevent="goToPage(pagination.page - 1)" :title="$t('pms.list.prev')">
              <span class="icon"><i class="fa fa-angle-left" aria-hidden="true"></i></span>
            </a>
            <a class="pagination-next" :class="{ 'is-disabled': loading || pagination.page >= totalPages }" @click.prevent="goToPage(pagination.page + 1)" :title="$t('pms.list.next')">
              <span class="icon"><i class="fa fa-angle-right" aria-hidden="true"></i></span>
            </a>
            <a class="pagination-next" :class="{ 'is-disabled': loading || pagination.page >= totalPages }" @click.prevent="goToPage(totalPages)" :title="$t('pms.list.last')">
              <span class="icon"><i class="fa fa-angle-double-right" aria-hidden="true"></i></span>
            </a>
            <ul class="pagination-list">
              <li><span class="pagination-link is-current">{{ pagination.page }} / {{ totalPages }}</span></li>
            </ul>
          </nav>
        </div>
      </div>
      <div class="table-container">
        <table class="table is-fullwidth is-striped is-hoverable">
          <thead>
            <tr>
              <th @click="setSort('external_id')" class="is-clickable">
                {{ $t('pms.list.columns.externalId') }}
                <span class="icon"><i class="fa" :class="sortIcon('external_id')"></i></span>
              </th>
              <th @click="setSort('name')" class="is-clickable">
                {{ $t('pms.list.columns.name') }}
                <span class="icon"><i class="fa" :class="sortIcon('name')"></i></span>
              </th>
              <th @click="setSort('min_persons')" class="is-clickable">
                {{ $t('pms.list.columns.minPersons') }}
                <span class="icon"><i class="fa" :class="sortIcon('min_persons')"></i></span>
              </th>
              <th @click="setSort('max_persons')" class="is-clickable">
                {{ $t('pms.list.columns.maxPersons') }}
                <span class="icon"><i class="fa" :class="sortIcon('max_persons')"></i></span>
              </th>
              <th @click="setSort('max_pets')" class="is-clickable">
                {{ $t('pms.list.columns.maxPets') }}
                <span class="icon"><i class="fa" :class="sortIcon('max_pets')"></i></span>
              </th>
              <th class="is-narrow has-text-centered is-clickable" @click="setSort('active')">
                {{ $t('pms.list.columns.status') }}
                <span class="icon"><i class="fa" :class="sortIcon('active')"></i></span>
              </th>
              <th @click="setSort('attributes')" class="is-clickable">
                {{ $t('pms.list.columns.attributes') }}
                <span class="icon"><i class="fa" :class="sortIcon('attributes')"></i></span>
              </th>
              <th @click="setSort('suitables')" class="is-clickable">
                {{ $t('pms.list.columns.suitables') }}
                <span class="icon"><i class="fa" :class="sortIcon('suitables')"></i></span>
              </th>
              <th @click="setSort('campsites')" class="is-clickable">
                {{ $t('pms.list.columns.campsites') }}
                <span class="icon"><i class="fa" :class="sortIcon('campsites')"></i></span>
              </th>
              <th class="has-text-centered">{{ $t('pms.list.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(it, idx) in items" :key="'row_'+(it.id || idx)">
              <td>{{ it.external_id }}</td>
              <td>
                <router-link
                  v-if="it && it.id"
                  :to="{ name: 'pms-accommodations-detail', params: { lang: lang, id: it.id }, query: { page: pagination.page } }"
                  class="has-text-link"
                  :title="$t('pms.list.tooltips.edit')"
                >
                  {{ (it.translations && it.translations[lang] && it.translations[lang].name) || '' }}
                </router-link>
                <span v-else>
                  {{ (it.translations && it.translations[lang] && it.translations[lang].name) || '' }}
                </span>
              </td>
              <td>{{ it.min_persons != null ? it.min_persons : '' }}</td>
              <td>{{ it.max_persons != null ? it.max_persons : '' }}</td>
              <td>{{ it.max_pets != null ? it.max_pets : '' }}</td>
              <td>
                <span class="icon" :title="it.active ? $t('generic.active') : $t('generic.inactive')">
                  <i style="font-size: medium" class="fa" :class="it.active ? 'fa-check-circle is-valid' : 'fa-minus-circle is-invalid'" aria-hidden="true"></i>
                </span>
              </td>
              <td>
                <a href="#" @click.prevent="openAttributesModal(idx)">
                  {{ Array.isArray(it.attributes) ? it.attributes.length : 0 }}
                </a>
              </td>
              <td>
                <a href="#" @click.prevent="openSuitablesModal(idx)">
                  {{ Array.isArray(it.suitables) ? it.suitables.length : 0 }}
                </a>
              </td>
              <td>{{ Array.isArray(it.accommodation_typ) ? it.accommodation_typ.length : 0 }}</td>
              <td class="has-text-right">
                <router-link class="icon is-small has-text-grey" :to="{ name: 'pms-accommodations-detail', params: { lang: lang, id: it.id }, query: { page: pagination.page } }" :title="$t('pms.list.tooltips.edit')" style="margin-right:.25rem;">
                  <i class="fa fa-pencil-square-o fa-lg btn-edit" aria-hidden="true"></i>
                </router-link>
                <a class="icon is-small has-text-grey" @click.prevent="copyItem(it)" :title="$t('pms.list.tooltips.copy')" style="margin-right:.25rem;">
                  <i class="fa fa-copy fa-lg btn-copy" aria-hidden="true"></i>
                </a>
                <a class="icon is-small has-text-danger" @click.prevent="openDeleteModal(idx)" :title="$t('pms.list.tooltips.delete')">
                  <i class="fa fa-trash fa-lg btn-delete" aria-hidden="true"></i>
                </a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="columns" v-if="pagination.count > pagination.pageSize">
        <div class="column is-12">
          <nav class="pagination is-right" role="navigation" aria-label="pagination">
            <a class="pagination-previous" :class="{ 'is-disabled': loading || pagination.page <= 1 }" @click.prevent="goToPage(1)" :title="$t('pms.list.first')">
              <span class="icon"><i class="fa fa-angle-double-left" aria-hidden="true"></i></span>
            </a>
            <a class="pagination-previous" :class="{ 'is-disabled': loading || pagination.page <= 1 }" @click.prevent="goToPage(pagination.page - 1)" :title="$t('pms.list.prev')">
              <span class="icon"><i class="fa fa-angle-left" aria-hidden="true"></i></span>
            </a>
            <a class="pagination-next" :class="{ 'is-disabled': loading || pagination.page >= totalPages }" @click.prevent="goToPage(pagination.page + 1)" :title="$t('pms.list.next')">
              <span class="icon"><i class="fa fa-angle-right" aria-hidden="true"></i></span>
            </a>
            <a class="pagination-next" :class="{ 'is-disabled': loading || pagination.page >= totalPages }" @click.prevent="goToPage(totalPages)" :title="$t('pms.list.last')">
              <span class="icon"><i class="fa fa-angle-double-right" aria-hidden="true"></i></span>
            </a>
            <ul class="pagination-list">
              <li><span class="pagination-link is-current">{{ pagination.page }} / {{ totalPages }}</span></li>
            </ul>
          </nav>
        </div>
      </div>
    </div>

    <!-- Delete modal -->
    <div class="modal isActive" :class="{ 'is-active': modal.isActive }">
      <div class="modal-background" @click="closeDeleteModal"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Löschen bestätigen</p>
          <button class="delete" @click="closeDeleteModal"></button>
        </header>
        <section class="modal-card-body">
          Möchtest du dieses Objekt wirklich löschen?
        </section>
        <footer class="modal-card-foot is-clearfix">
          <a class="button" @click="closeDeleteModal">Abbrechen</a>
          <a class="button is-danger" @click="deleteItem()">
            <span class="icon"><i class="fa fa-trash" /></span>
            <span>Löschen</span>
          </a>
        </footer>
      </div>
    </div>

    <!-- Attributes modal (view/edit) -->
    <attributes
      v-if="attributesModal.isActive && currentEditItem"
      :all-attributes="allAttributes"
      :active-attributes="getActiveAttributes(currentEditItem)"
      :accommodation-type="''"
      @toggle-modal="closeAttributesModal"
      @save="saveAttributesModal"
    />

    <!-- Suitables modal (view/edit) -->
    <suitables
      v-if="suitablesModal.isActive && currentEditItem"
      :all-suitables="allSuitables"
      :active-suitables="getActiveSuitables(currentEditItem)"
      @toggle-modal="closeSuitablesModal"
      @save="saveSuitablesModal"
    />
  </div>
</template>

<script>
    import { Api } from '../../../api';
    import { AppModel } from '../../../models';
    import { Tracking } from '../../../utils';
    import Attributes from '../../components/attributes';
    import Suitables from '../../components/suitables';

    export default {
        name: 'pms-accommodations',
        components: { Attributes, Suitables },
        data() {
            return {
                items: null,
                allItems: [], // for client-side fallback
                allAttributes: [],
                attributeCategories: {},
                allSuitables: [],
                isAllCollapsed: false,
                modal: { isActive: false, index: null },
                // inline edit modals
                attributesModal: { isActive: false, index: null },
                suitablesModal: { isActive: false, index: null },
                currentEditItem: null,
                modalLoading: false,
                loadError: null,
                loading: false,
                // pagination + filters
                pagination: {
                    page: 1,
                    pageSize: 10,
                    count: 0,
                },
                filters: {
                    search: '',
                    active: '', // '', 'true', 'false'
                    unassigned: '', // '', 'unassigned', 'assigned'
                },
                sortBy: '', // '', 'external_id', 'name', 'min_persons', 'max_persons', 'max_pets', 'active', 'attributes', 'suitables', 'campsites'
                sortDir: 'asc', // 'asc' | 'desc'
                clientSideMode: false,
                _filterTimer: null,
            };
        },
        computed: {
            lang() {
                return AppModel.lang;
            },
            totalPages() {
                const ps = this.pagination.pageSize || 1;
                const c = this.pagination.count || (this.items ? this.items.length : 0) || 0;
                return Math.max(1, Math.ceil(c / ps));
            }
        },
        async created() {
            // Initialize pagination from route query to preserve current page when navigating back
            const qp = this.$route && this.$route.query ? this.$route.query.page : null;
            const initialPage = qp ? parseInt(qp, 10) : null;
            if(!isNaN(initialPage) && initialPage > 0) {
                this.pagination.page = initialPage;
            }
            await this.bootstrap();
            await this.fetchAll();
        },
        beforeRouteUpdate(to, from, next) {
            // React to page changes in the query (e.g., browser back/forward)
            const qp = to && to.query ? to.query.page : null;
            const p = qp ? parseInt(qp, 10) : null;
            if(!isNaN(p) && p > 0) {
                this.pagination.page = p;
            } else {
                this.pagination.page = 1;
            }
            if(this.clientSideMode) {
                this.applyClientSide();
                next();
            } else {
                this.fetchAll().then(() => next()).catch(() => next());
            }
        },
        methods: {
            syncPageToRoute(page) {
                try {
                    const q = Object.assign({}, (this.$route && this.$route.query) || {}, { page: String(page) });
                    if(this.$router) this.$router.replace({ query: q }).catch(() => {});
                } catch (e) {}
            },
            sortIcon(key) {
                if(this.sortBy !== key) return 'fa-sort';
                return this.sortDir === 'asc' ? 'fa-sort-asc' : 'fa-sort-desc';
            },
            setSort(key) {
                if(this.sortBy === key) {
                    this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
                } else {
                    this.sortBy = key;
                    this.sortDir = 'asc';
                }
                this.pagination.page = 1;
                this.syncPageToRoute(1);
                if(this.clientSideMode) {
                    this.applyClientSide();
                } else {
                    this.fetchAll();
                }
            },
            // Inline edit modals for Attributes/Suitables
            openAttributesModal(index) {
                this.attributesModal = { isActive: true, index: index };
                this.currentEditItem = this.items[index];
            },
            closeAttributesModal() {
                this.attributesModal = { isActive: false, index: null };
                this.currentEditItem = null;
            },
            openSuitablesModal(index) {
                this.suitablesModal = { isActive: true, index: index };
                this.currentEditItem = this.items[index];
            },
            closeSuitablesModal() {
                this.suitablesModal = { isActive: false, index: null };
                this.currentEditItem = null;
            },
            getActiveAttributes(item) {
                // Build full objects for selected attribute IDs
                const selected = Array.isArray(item && item.attributes) ? item.attributes : [];
                const selectedIds = selected.map(function(v) { return (typeof v === 'object' ? v.id : v); });
                const flat = [];
                (this.allAttributes || []).forEach(function(cat) {
                    if(cat && Array.isArray(cat.attributes)) {
                        cat.attributes.forEach(function(a) { flat.push(a); });
                    }
                });
                return flat.filter(function(a) { return selectedIds.indexOf(a.id) !== -1; });
            },
            getActiveSuitables(item) {
                const ids = Array.isArray(item && item.suitables) ? item.suitables : [];
                const idSet = ids.reduce(function(acc, v) { acc[String(typeof v === 'object' ? v.id : v)] = true; return acc; }, {});
                return (this.allSuitables || []).filter(function(s) { return idSet[String(s.id)]; });
            },
            async saveAttributesModal(ids) {
                if(this.attributesModal.index === null) return;
                const idx = this.attributesModal.index;
                const updated = Object.assign({}, this.items[idx], { attributes: Array.isArray(ids) ? ids : [] });
                try {
                    this.modalLoading = true;
                    await Api.pmsAccommodations.updatePmsAccommodation(updated);
                    // update local state
                    this.$set(this.items, idx, updated);
                    this.closeAttributesModal();
                    // optionally toast
                    if(this.$buefy && this.$buefy.toast) { this.$buefy.toast.open({ message: 'Attribute gespeichert', type: 'is-success' }); }
                } catch (e) {
                    console.error(e);
                    if(this.$buefy && this.$buefy.toast) { this.$buefy.toast.open({ message: 'Speichern fehlgeschlagen', type: 'is-danger' }); }
                } finally {
                    this.modalLoading = false;
                }
            },
            async saveSuitablesModal(ids) {
                if(this.suitablesModal.index === null) return;
                const idx = this.suitablesModal.index;
                const updated = Object.assign({}, this.items[idx], { suitables: Array.isArray(ids) ? ids : [] });
                try {
                    this.modalLoading = true;
                    await Api.pmsAccommodations.updatePmsAccommodation(updated);
                    this.$set(this.items, idx, updated);
                    this.closeSuitablesModal();
                    if(this.$buefy && this.$buefy.toast) { this.$buefy.toast.open({ message: 'Geeignet für gespeichert', type: 'is-success' }); }
                } catch (e) {
                    console.error(e);
                    if(this.$buefy && this.$buefy.toast) { this.$buefy.toast.open({ message: 'Speichern fehlgeschlagen', type: 'is-danger' }); }
                } finally {
                    this.modalLoading = false;
                }
            },
            async bootstrap() {
                await Api.bootstrap();
                // Load persisted page size preference
                try {
                    const raw = localStorage.getItem('pms_accommodations_page_size');
                    const n = raw != null ? Number(raw) : null;
                    const allowed = [10, 25, 50, 100];
                    if(n && allowed.indexOf(n) !== -1) {
                        this.pagination.pageSize = n;
                    }
                } catch (e) {}
            },
            async fetchAll() {
                try {
                    this.loadError = null;
                    this.loading = true;
                    const params = {
                        page: this.pagination.page,
                        page_size: this.pagination.pageSize,
                        q: this.filters.search,
                        active: this.filters.active,
                    };
                    // pass assignment filter to server if supported
                    if(this.filters.unassigned === 'unassigned') {
                        params.unassigned_only = '1';
                    } else if(this.filters.unassigned === 'assigned') {
                        params.assigned_only = '1';
                    }
                    // map sort to server ordering when possible
                    const serverSortable = ['external_id', 'name', 'min_persons', 'max_persons', 'max_pets', 'active'];
                    if(this.sortBy && serverSortable.indexOf(this.sortBy) !== -1) {
                        params.ordering = (this.sortDir === 'desc' ? '-' : '') + this.sortBy;
                    }
                    const [listRes, attrsRes, suitsRes] = await Promise.all([
                        Api.pmsAccommodations.fetchPmsAccommodations(params),
                        Api.attributes.getAllAccomodations(),
                        Api.suitables.getAllSuitables(),
                    ]);

                    // Determine server-side pagination or client-side fallback
                    const body = (listRes && listRes.data) || (listRes && listRes.body) || null;
                    if(body && typeof body === 'object' && (Array.isArray(body.results) || typeof body.count !== 'undefined')) {
                        // DRF-style
                        const results = Array.isArray(body.results) ? body.results : [];
                        this.items = results;
                        this.pagination.count = Number(body.count || results.length || 0);
                        this.clientSideMode = false;
                        // Ensure visible sorting on current page regardless of server behavior
                        if(this.sortBy) {
                            const sortBy = this.sortBy;
                            const dir = this.sortDir === 'desc' ? -1 : 1;
                            const l = AppModel.lang;
                            this.items = this.items.slice().sort(function(a, b) {
                                function getVal(it) {
                                    switch (sortBy) {
                                    case 'external_id':
                                        return (it && it.external_id) ? String(it.external_id).toLowerCase() : '';
                                    case 'name': {
                                        if(it && it.name) return String(it.name).toLowerCase();
                                        const tr2 = it && it.translations;
                                        if(tr2 && tr2[l] && tr2[l].name) return String(tr2[l].name).toLowerCase();
                                        return '';
                                    }
                                    case 'min_persons':
                                        return typeof it.min_persons === 'number' ? it.min_persons : (it.min_persons == null ? -Infinity : Number(it.min_persons));
                                    case 'max_persons':
                                        return typeof it.max_persons === 'number' ? it.max_persons : (it.max_persons == null ? -Infinity : Number(it.max_persons));
                                    case 'max_pets':
                                        return typeof it.max_pets === 'number' ? it.max_pets : (it.max_pets == null ? -Infinity : Number(it.max_pets));
                                    case 'active':
                                        return it && it.active ? 1 : 0;
                                    case 'attributes':
                                        return Array.isArray(it.attributes) ? it.attributes.length : 0;
                                    case 'suitables':
                                        return Array.isArray(it.suitables) ? it.suitables.length : 0;
                                    case 'campsites':
                                        return Array.isArray(it.accommodation_typ) ? it.accommodation_typ.length : 0;
                                    default:
                                        return 0;
                                    }
                                }
                                const va = getVal(a);
                                const vb = getVal(b);
                                if(va < vb) return -1 * dir;
                                if(va > vb) return 1 * dir;
                                return 0;
                            });
                        }
                    } else {
                        // Array fallback
                        const arrayData = Array.isArray(body) ? body : [];
                        this.allItems = arrayData;
                        this.clientSideMode = true;
                        this.applyClientSide();
                    }

                    // collapse all by default to make large lists manageable
                    try {
                        await this.$nextTick();
                        for(let i = 0; i < (this.items ? this.items.length : 0); i++) {
                            const r = this.$refs[`item-${i}`];
                            if(r && r[0] && typeof r[0].setCollapsed === 'function') {
                                r[0].setCollapsed(true);
                            }
                        }
                        this.isAllCollapsed = true;
                    } catch (e) {}

                    const allAttrs = (attrsRes && attrsRes.data) || (attrsRes && attrsRes.body) || [];
                    this.allAttributes = allAttrs;
                    // Build attributeCategories map similar to campsites page
                    const categories = {};
                    allAttrs.forEach(function(cat) { categories[cat.id] = cat; });
                    this.attributeCategories = categories;

                    this.allSuitables = (suitsRes && suitsRes.data) || (suitsRes && suitsRes.body) || [];
                } catch (e) {
                    console.error(e);
                    this.loadError = 'Konnte Daten nicht laden. API noch nicht verfügbar?';
                    this.items = [];
                } finally {
                    this.loading = false;
                }
            },
            applyClientSide() {
                // Filter and paginate allItems into items
                let list = Array.isArray(this.allItems) ? this.allItems.slice() : [];
                // search
                const q = (this.filters.search || '').toLowerCase();
                if(q) {
                    list = list.filter(function(x) {
                        const tr = x && x.translations;
                        const l = AppModel.lang;
                        // prefer current language name/desc, but fallback to any available translation
                        let name = '';
                        let desc = '';
                        if(tr && tr[l]) {
                            name = tr[l] && tr[l].name ? String(tr[l].name).toLowerCase() : '';
                            desc = tr[l] && tr[l].description ? String(tr[l].description).toLowerCase() : '';
                        }
                        if(!name && tr && typeof tr === 'object') {
                            const keys = Object.keys(tr);
                            for(let i = 0; i < keys.length; i++) {
                                const t = tr[keys[i]];
                                if(t && t.name) { name = String(t.name).toLowerCase(); break; }
                            }
                        }
                        const ext = x && x.external_id ? String(x.external_id).toLowerCase() : '';
                        return (name && name.indexOf(q) !== -1) || (desc && desc.indexOf(q) !== -1) || (ext && ext.indexOf(q) !== -1);
                    });
                }
                // active filter
                if(this.filters.active === 'true') {
                    list = list.filter(function(x) { return !!x.active; });
                } else if(this.filters.active === 'false') {
                    list = list.filter(function(x) { return !x.active; });
                }
                // assignment filter (tri-state)
                if(this.filters.unassigned === 'unassigned') {
                    list = list.filter(function(x) { return !Array.isArray(x.accommodation_typ) || x.accommodation_typ.length === 0; });
                } else if(this.filters.unassigned === 'assigned') {
                    list = list.filter(function(x) { return Array.isArray(x.accommodation_typ) && x.accommodation_typ.length > 0; });
                }
                // sorting
                const sortBy = this.sortBy;
                const dir = this.sortDir === 'desc' ? -1 : 1;
                if(sortBy) {
                    const l = AppModel.lang;
                    list.sort(function(a, b) {
                        function getVal(it) {
                            switch (sortBy) {
                            case 'external_id':
                                return (it && it.external_id) ? String(it.external_id).toLowerCase() : '';
                            case 'name': {
                                if(it && it.name) return String(it.name).toLowerCase();
                                const tr2 = it && it.translations;
                                if(tr2 && tr2[l] && tr2[l].name) return String(tr2[l].name).toLowerCase();
                                return '';
                            }
                            case 'min_persons':
                                return typeof it.min_persons === 'number' ? it.min_persons : (it.min_persons == null ? null : Number(it.min_persons));
                            case 'max_persons':
                                return typeof it.max_persons === 'number' ? it.max_persons : (it.max_persons == null ? null : Number(it.max_persons));
                            case 'max_pets':
                                return typeof it.max_pets === 'number' ? it.max_pets : (it.max_pets == null ? null : Number(it.max_pets));
                            case 'active':
                                return it && it.active ? 1 : 0;
                            case 'attributes':
                                return Array.isArray(it.attributes) ? it.attributes.length : 0;
                            case 'suitables':
                                return Array.isArray(it.suitables) ? it.suitables.length : 0;
                            case 'campsites':
                                return Array.isArray(it.accommodation_typ) ? it.accommodation_typ.length : 0;
                            default:
                                return 0;
                            }
                        }
                        const va = getVal(a);
                        const vb = getVal(b);
                        const aNull = (va === null || typeof va === 'undefined' || (typeof va === 'number' && isNaN(va)));
                        const bNull = (vb === null || typeof vb === 'undefined' || (typeof vb === 'number' && isNaN(vb)));
                        if(aNull && !bNull) return 1; // nulls last
                        if(!aNull && bNull) return -1;
                        if(va < vb) return -1 * dir;
                        if(va > vb) return 1 * dir;
                        return 0;
                    });
                }
                this.pagination.count = list.length;
                const start = (this.pagination.page - 1) * this.pagination.pageSize;
                const end = start + this.pagination.pageSize;
                this.items = list.slice(start, end);
            },
            onFilterChange() {
                // debounce
                const self = this;
                if(this._filterTimer) {
                    clearTimeout(this._filterTimer);
                }
                this._filterTimer = setTimeout(function() {
                    self.pagination.page = 1;
                    if(self.syncPageToRoute) self.syncPageToRoute(1);
                    if(self.clientSideMode) {
                        self.applyClientSide();
                    } else {
                        self.fetchAll();
                    }
                }, 400);
            },
            onPageSizeChange() {
                try { localStorage.setItem('pms_accommodations_page_size', String(this.pagination.pageSize || '')); } catch (e) {}
                this.pagination.page = 1;
                this.syncPageToRoute(1);
                if(this.clientSideMode) {
                    this.applyClientSide();
                } else {
                    this.fetchAll();
                }
            },
            goToPage(page) {
                const totalPages = Math.max(1, Math.ceil(this.pagination.count / this.pagination.pageSize));
                if(page < 1) page = 1;
                if(page > totalPages) page = totalPages;
                // Always sync to route so the URL reflects the current page
                this.syncPageToRoute(page);
                if(page === this.pagination.page) return;
                this.pagination.page = page;
                if(this.clientSideMode) {
                    this.applyClientSide();
                } else {
                    this.fetchAll();
                }
            },
            addItem() {
                const empty = {
                    id: null,
                    active: true,
                    external_id: '',
                    min_persons: null,
                    max_persons: null,
                    max_pets: null,
                    length: null,
                    width: null,
                    area: null,
                    accommodation_typ: [],
                    attributes: [],
                    suitables: [],
                    translations: { [this.lang]: { name: '', description: '' } },
                    isCollapsed: false,
                };
                if(!Array.isArray(this.items)) {
                    this.items = [];
                }
                // Insert new item at the top
                this.items = [empty].concat(this.items);
                this.$nextTick(() => {
                    const ref = this.$refs[`item-0`];
                    if(ref && ref[0] && ref[0].$el) {
                        try {
                            ref[0].$el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        } catch (e) {}
                    }
                });
            },
            async saveItem(index) {
                const payload = Object.assign({}, this.items[index]);

                try {
                    Tracking.trackPerformanceStart('pms-accommodations');
                    if(payload.id) {
                        await Api.pmsAccommodations.updatePmsAccommodation(payload);
                    } else {
                        await Api.pmsAccommodations.addPmsAccommodation(payload);
                    }
                    Tracking.trackPerformanceEnd('pms-accommodations', payload.id ? 'put' : 'post');

                    // Refresh list to sync server state
                    await this.fetchAll();
                } catch (e) {
                    console.error(e);
                    Tracking.canceltrackPerformance('pms-accommodations');
                    if(this.$buefy && this.$buefy.toast) {
                        this.$buefy.toast.open({ message: 'Speichern fehlgeschlagen', type: 'is-danger' });
                    }
                }
            },
            openDeleteModal(index) {
                this.modal = { isActive: true, index: index };
            },
            async copyItem(item) {
                try {
                    const lang = AppModel.lang;
                    const src = item || {};
                    const clone = JSON.parse(JSON.stringify(src));
                    // prepare draft (do NOT save yet)
                    delete clone.id;
                    if(!clone.translations) { clone.translations = {}; }
                    if(!clone.translations[lang]) { clone.translations[lang] = { name: '', description: '' }; }
                    const oldName = clone.translations[lang].name || '';
                    const oldExt = clone.external_id || '';
                    const prefix = (this.$t && this.$t('pms.copyPrefix')) || 'Copy of ';
                    clone.translations[lang].name = prefix + oldName;
                    clone.external_id = prefix + oldExt;
                    clone.campsite_ids = Array.isArray(clone.campsite_ids) ? clone.campsite_ids.slice() : [];
                    if(Array.isArray(clone.accommodation_typ) && !clone.campsite_ids.length) {
                        clone.campsite_ids = clone.accommodation_typ.slice();
                    }
                    clone.attributes = Array.isArray(clone.attributes) ? clone.attributes.slice() : [];
                    clone.suitables = Array.isArray(clone.suitables) ? clone.suitables.slice() : [];
                    // provide flat fields for non-parler backends (will be sanitized in Api on save)
                    clone.name = clone.translations[lang].name;
                    clone.description = (clone.translations[lang] && clone.translations[lang].description) || '';
                    delete clone.accommodation_typ;
                    // store as transient draft and navigate to NEW route
                    Api.pmsDraft = clone;
                    this.$router.push({ name: 'pms-accommodations-new', params: { lang: this.lang }, query: { page: (this.$route && this.$route.query && this.$route.query.page) || String(this.pagination.page || 1) } });
                } catch (e) {
                    console.error(e);
                    if(this.$buefy && this.$buefy.toast) { this.$buefy.toast.open({ message: 'Kopieren fehlgeschlagen', type: 'is-danger' }); }
                }
            },
            closeDeleteModal() {
                this.modal = { isActive: false, index: null };
            },
            async deleteItem() {
                const idx = this.modal.index;
                if(idx === null || idx === undefined) return;
                const item = this.items[idx];
                try {
                    await Api.pmsAccommodations.deletePmsAccommodation({ id: item.id });
                    this.items.splice(idx, 1);
                } catch (e) {
                    console.error(e);
                    if(this.$buefy && this.$buefy.toast) {
                        this.$buefy.toast.open({ message: 'Löschen fehlgeschlagen', type: 'is-danger' });
                    }
                } finally {
                    this.closeDeleteModal();
                }
            },
            collapseAll() {
                this.isAllCollapsed = !this.isAllCollapsed;
                this.$nextTick(() => {
                    for(let i = 0; i < (this.items ? this.items.length : 0); i++) {
                        const r = this.$refs[`item-${i}`];
                        if(r && r[0] && typeof r[0].setCollapsed === 'function') {
                            r[0].setCollapsed(this.isAllCollapsed);
                        }
                    }
                });
            }
        }
    };
</script>

<style scoped>
.pms-item { margin-bottom: 1rem; }
.btn-copy {
    color: darkblue;
    font-size: medium;
}
.btn-edit {
    color: black;
    font-size: medium;
}
.btn-delete {
    color: red;
    font-size: medium;
}
/* Make sort icons in table headers a bit smaller */
.table thead th .icon { width: 0.9em; height: 0.9em; }
.table thead th .icon .fa { font-size: 0.8em; opacity: 0.8; margin-left: 0.25rem; }
</style>



// WEBPACK FOOTER //
// src/views/pages/pms-accommodations/pms-accommodations.vue